// Imports ------------------------------------------------------------------->

import {Service, service, inject} from "./di";
import {https} from "../utils/http";
import * as array from "../utils/array";
import {EcomItem} from "../services/EcomService";
import {Timer} from "../utils/Timer";
import {fetch, FetchError, FetchOptions} from "../utils/fetch";
import {i18n} from "../i18n/I18n";
import {ShoppingListOp, ClippingItemDetails, SLSyncable, Clipping, ClippingType,
				ShoppingListOpVerb, SLQuickItem, AccountsUser, ToastLevel} from "../definitions/CommonDefinitions";
import {EcomItemSummaryJSON} from "../definitions/BackflippDefinitions";
import {StorageService} from "../services/StorageService";
import {LocationService} from "../services/LocationService";
import {EventService} from "../services/EventService";
import {AuthenticationService} from "../services/AuthenticationService";
import {AuthStatus} from "../services/definitions/IAuthenticationService";
import {ConfigService} from "../services/ConfigService";
import {CategoryService} from "../services/CategoryService";
import {ToastService} from "../services/ToastService";
import {Backflipp} from "../services/BackflippService";

// Private Definitions ------------------------------------------------------->

const MAX_UPDATE_RETRY = 3;

interface ShoppingListModify {
	commit_version: number;
	_ops: ShoppingListOp[];
}

// Public Definitions -------------------------------------------------------->

export interface SuggestionsJSON {
	recommendations: any[];
	history: HistAddJSON[];
}

export interface HistAddJSON {
	canonical_category: string;
	name: string;
	icon_url: string;
	icon_asset_name: string;
	category: string;
	date: string;
}

export class SLSuggestion {
	canonicalCategory: string; // NOTE: this value is locale INDEPENDENT (e.g. "dairy_eggs" even if locale is FRENCH!)
	name: string;
	iconUrl: string;
	iconAssetName: string;
	category: string;
	date: Date;

	constructor(json: HistAddJSON) {
		this.canonicalCategory = json.canonical_category;
		this.name = json.name;
		this.iconUrl = https(json.icon_url);
		this.iconAssetName = json.icon_asset_name;
		this.category = json.category;
		this.date = new Date(json.date);
	}
}

export interface InviteJSON {
	invite_id: string;
	invite_url: string;
}

/* Item details adapter for Item Clippings. Historically, item clippings have been managed
	* by the ClippingService, but this changed once Shopping List sync was introduced.
	* Our code assumes that all Clippings will implement the Clipping interface, so it makes sense
	* for our SLFlyerItemClipping class to also implement Clipping (see below). This interface defines
	* the extra details associated with Clipping objects with type equal to ITEM */
export interface SLItemClippingDetails extends ClippingItemDetails {
	details: {
		flyerId: number,
		top: number,
		bottom: number,
		left: number,
		right: number
	};
}

export class SLFlyerItemClipping implements SLSyncable, Clipping {
	id: string;
	commitVersion: number;
	flyerItemId: number;
	name: string;
	flyerId: number;
	right: number;
	left: number;
	top: number;
	bottom: number;
	price: number;
	merchantName: string;
	merchantLogoUrl: string;
	thumbnailUrl: string;
	validTo: string;

	/* Implement functions for Clipping interface */
	get type() { return ClippingType.ITEM; }
	get merchant() { return this.merchantName; }
	get merchantLogo() { return this.merchantLogoUrl; }
	get item() {
		return {
			id: `${this.flyerItemId}`,
			validTo: this.validTo,
			imageUrl: this.thumbnailUrl,
			details: {
				flyerId: this.flyerId,
				top: this.top,
				bottom: this.bottom,
				left: this.left,
				right: this.right
			}
		};
	}

	constructor(json: any) {
		this.id = json.id;
		this.commitVersion = json.commitVersion || json.commit_version;
		this.flyerItemId = json.flyerItemId || json.flyer_item_id;
		this.name = json.name;
		this.flyerId = json.flyerId || json.flyer_id;
		this.right = json.right;
		this.left = json.left;
		this.top = json.top;
		this.bottom = json.bottom;
		this.price = json.price;
		this.merchantName = json.merchantName || json.merchant_name;
		this.merchantLogoUrl = https(json.merchantLogoUrl || json.merchant_logo_url);
		this.thumbnailUrl = https(json.thumbnailUrl || json.thumbnail_url);
		this.validTo = json.validTo || json.valid_to;
	}

	createOp(verb: ShoppingListOpVerb, modify?: boolean): ShoppingListOp {
		return {
			verb,
			object: {
				id: modify ? this.id : null,
				commit_version: modify ? this.commitVersion : null,
				type: "flyer_item_clipping",

				flyer_item_id: this.flyerItemId,
				name: this.name,
				flyer_id: this.flyerId,
				right: this.right,
				left: this.left,
				top: this.top,
				bottom: this.bottom,
				price: this.price,
				merchant_name: this.merchantName,
				merchant_logo_url: this.merchantLogoUrl,
				thumbnail_url: this.thumbnailUrl,
				valid_to: this.validTo
			}
		};

	}
}

export class SLListItem implements SLSyncable {
	id: string;
	commitVersion: number;
	term: string;
	checked: boolean;

	constructor(json: any) {
		this.id = json.id;
		this.commitVersion = json.commitVersion || json.commit_version;
		this.term = json.term;
		this.checked = json.checked;
	}

	createOp(verb: ShoppingListOpVerb, modify?: boolean): ShoppingListOp {
		return {
			verb,
			object: {
				id: modify ? this.id : null,
				commit_version: modify ? this.commitVersion : null,
				type: "list_item",
				term: this.term,
				checked: this.checked
			}
		};
	}
}

export class SLPhoto implements SLSyncable {
	id: string;
	commitVersion: number;
	points: {x: number, y: number}[];
	fullImgUrl: string;
	smallImgUrl: string;
	fileId: string;
	transcriptionStatus: string; // "PENDING", "SUCCESS", "ERROR"
	x: number;
	y: number;
	transcriptions: string[];

	constructor(json: any) {
		this.id = json.id;
		this.commitVersion = json.commitVersion || json.commit_version;
		this.points = json.points;
		this.fullImgUrl = https(json.fullImgUrl || json.full_img_url);
		this.smallImgUrl = https(json.smallImgUrl || json.small_img_url);
		this.fileId = json.fileId || json.file_id;
		this.transcriptionStatus = json.transcriptionStatus || json.transcription_status;
		this.x = json.x;
		this.y = json.y;
		this.transcriptions = json.transcriptions;
	}

	createOp(verb: ShoppingListOpVerb): ShoppingListOp {
		if (verb === "post") {
			return null; // Can't 'post' photos from the web
		}
		return {
			verb,
			object: {
				type: "photo",
				id: this.id,
				commit_version: this.commitVersion
			}
		};
	}
}

export class SLEcomItem implements SLSyncable, Clipping {
	id: string;
	commitVersion: number;
	ecomItemId: number;
	name: string;
	price: number;
	merchantName: string;
	merchantId: number;
	merchantLogoUrl: string;
	thumbnailUrl: string;
	ctaLabel: string;
	ctaUrl: string;
	expired: boolean;

	constructor(json: any) {
		this.id = json.id;
		this.commitVersion = json.commitVersion || json.commit_version;
		this.ecomItemId = json.ecom_item_id || json.ecomItemId;
		this.name = json.name;
		this.price = json.price;
		this.merchantName = json.merchant_name || json.merchantName;
		this.merchantId = json.merchant_id || json.merchantId;
		this.merchantLogoUrl = json.merchant_logo_url || json.merchantLogoUrl;
		this.thumbnailUrl = json.thumbnail_url || json.thumbnailUrl;
		this.ctaLabel = json.cta_label || json.ctaLabel;
		this.ctaUrl = json.cta_url || json.ctaUrl;
		this.expired = json.expired;
	}

	get type() {
		return ClippingType.ECOM;
	}

	get merchant() {
		return this.merchantName;
	}

	get merchantLogo() {
		return this.merchantLogoUrl;
	}

	get item() {
		return {
			id: `${this.ecomItemId}`,
			validTo: null,
			imageUrl: this.thumbnailUrl
		} as ClippingItemDetails;
	}

	createOp(verb: ShoppingListOpVerb, modify?: boolean): ShoppingListOp {
		return {
			verb,
			object: {
				id: modify ? this.id : null,
				commit_version: modify ? this.commitVersion : null,
				type: "ecom_item_clipping",
				ecom_item_id: this.ecomItemId,
				name: this.name,
				price: `${this.price}`,
				merchant_name: this.merchantName,
				merchant_id: `${this.merchantId}`,
				merchant_logo_url: this.merchantLogoUrl,
				thumbnail_url: this.thumbnailUrl,
				cta_label: this.ctaLabel,
				cta_url: this.ctaUrl,
				expired: `${this.expired}`
			}
		};
	}

	adapt() {
		/* Helper function to adapt SLEcomItem items into EcomItems. This is
		 * handy when we want to display saved Ecom items as a tile in the UI
		 * using the EcomItemTile component */
		return new EcomItem({
			ecom_item: {
				id: this.ecomItemId,
				name: this.name,
				current_price: this.price,
				merchant: this.merchant,
				merchant_id: this.merchantId,
				merchant_logo: this.merchantLogoUrl,
				url: this.ctaUrl,
				media: [{
					media_type: "image",
					media_name: "large",
					url: this.thumbnailUrl
				}],
				expired: this.expired
			}
		});
	}
}

export class ShoppingList implements SLSyncable {
	@inject storage: StorageService;

	id: string;
	commitVersion: number;
	flyerItemClippings: SLFlyerItemClipping[];
	listItems: SLListItem[];
	photos: SLPhoto[];
	ecomItems: SLEcomItem[];
	users: {id: string}[];

	private _delegate: boolean;
	private _outstandingOps: ShoppingListOp[] = [];
	private _changeCallback: () => void = null;

	get items() {
		return this.listItems;
	}

	get flyerItems() {
		return this.flyerItemClippings;
	}

	get modified() {
		return this._outstandingOps.length > 0;
	}

	get isDelegate() {
		return this._delegate;
	}

	get size() {
		return this.flyerItems.length + this.items.length + this.photos.length + this.ecomItems.length;
	}

	constructor(json: any, proxy: boolean, cb: () => void) {
		const clippingItems = json.flyerItemClippings || json.flyer_item_clippings;
		const listItems = json.listItems || json.list_items;
		const ecomItems = json.ecom_item_clippings || json.ecomItems;
		this.id = json.id;
		this.commitVersion = json.commitVersion || json.commit_version;
		this.flyerItemClippings = clippingItems ? clippingItems.map((item: any) => new SLFlyerItemClipping(item)) : [];
		this.listItems = listItems ? listItems.map((item: any) => new SLListItem(item)) : [];
		this.photos = json.photos ? json.photos.map((item: any) => new SLPhoto(item)) : [];
		this.ecomItems = ecomItems ? ecomItems.map((item: any) => new SLEcomItem(item)) : [];
		this.users = json.users || [];
		this._delegate = json._proxy || proxy;
		this._sortTerms();

		this._changeCallback = cb;
	}

	localSave() {
		const data = Object.assign({}, this);
		delete data.users;

		this.storage.setItem("shopping_list", JSON.stringify(data));
		Cookies.set("users", JSON.stringify(this.users), { expires: 365 });
	}

	add(items: SLFlyerItemClipping[]): void;
	add(items: SLListItem[]): void;
	add(items: SLEcomItem[]): void;
	add(items: SLSyncable[]): void {
		let sortItems = false;
		items.forEach((item) => {
			const arr = this._getArrayForItem(item);
			arr.push(item);
			sortItems = sortItems || item instanceof SLListItem;
			this._addOperation(item.createOp("post"));
		});
		if (sortItems) {
			/* Sort the list items as we add them */
			this._sortTerms();
		}
		if (this._changeCallback) {
			this._changeCallback();
		}
	}

	remove(items: SLFlyerItemClipping[]): boolean;
	remove(items: SLListItem[]): boolean;
	remove(items: SLPhoto[]): boolean;
	remove(items: SLEcomItem[]): boolean;
	remove(items: SLSyncable[]): boolean;
	remove(items: SLSyncable[]): boolean {
		let result = false;
		items.forEach((item) => {
			const arr = this._getArrayForItem(item);
			const matchIdx = array.findIndex(arr, (entry) => entry.id === item.id);
			if (matchIdx >= 0) {
				arr.splice(matchIdx, 1);
				result = true;
				this._addOperation(item.createOp("delete", true));
			}
		});
		if (this._changeCallback) {
			this._changeCallback();
		}
		return result;
	}

	contains(item: SLFlyerItemClipping): boolean;
	contains(item: SLListItem): boolean;
	contains(item: SLPhoto): boolean;
	contains(item: SLEcomItem): boolean;
	contains(item: SLSyncable): boolean {
		const arr = this._getArrayForItem(item);
		return array.findIndex(arr, (entry) => item.id === entry.id) >= 0;
	}

	check(item: SLListItem, checked: boolean): boolean;
	check(item: SLSyncable, checked: boolean): boolean {
		const match = array.find(this.listItems, (entry) => item.id === entry.id);
		let result = false;
		if (match) {
			match.checked = checked;
			result = true;
			this._addOperation(match.createOp("post", true));
			if (this._changeCallback) {
				this._changeCallback();
			}
		}
		return result;
	}

	/* FIXME: type 'data' */
	modify(items: SLListItem[], data: any[]): boolean;
	modify(items: SLEcomItem[], data: any[]): boolean;
	modify(items: SLSyncable[], data: any[]): boolean {

		let result = false;
		items.forEach((item, idx) => {
			const arr = this._getArrayForItem(item);
			const match = array.find(arr, (entry) => item.id === entry.id);
			if (match) {
				/* FIXME: this is sloppy. this behaviour is specific to the SL entry type and
				* should be pushed down into the specific implementation class. */
				if (item instanceof SLListItem) {
					(match as SLListItem).term = data[idx];
				} else if (item instanceof SLEcomItem) {
					const target = item as SLEcomItem;
					target.ecomItemId = data[idx].id;
					target.price = data[idx].current_price;
					target.expired = data[idx].expired;
				}
				this._addOperation(match.createOp("post", true));
				result = true;
			}
		});

		if (result && this._changeCallback) {
			this._changeCallback();
		}

		return result;
	}

	popOperations() {
		const result = this._outstandingOps;
		this._outstandingOps = [];
		return result;
	}

	clear() {
		/* Clear any outstanding ops */
		this._outstandingOps = [];

		/* Generate delete events for all current items */
		this.listItems.forEach((item) => this._addOperation(item.createOp("delete", true)));
		/* Only remove the list items, not photo lists or clippings
			* This is weird behaviour, but it's so we're consistent with the behaviour in the app
			* See FW-702 */
		// this.flyerItemClippings.forEach((item) => this._addOperation(item.createOp('delete', true)));
		// this.photos.forEach((item) => this._addOperation(item.createOp('delete')));

		this.listItems = [];
		// this.flyerItemClippings = [];
		// this.photos = [];
		this.localSave();
		if (this._changeCallback) {
			this._changeCallback();
		}
	}

	mergeOperations(ops: ShoppingListOp[]) {
		/* Helper function to merge operations and resolve version conflicts in SL operations.
			* This is needed to handle cases where we get an item conflict updating a shopping list.
			*  What we want to do here is convert old SL ops into new ones with an updated commit_version */
		const existingItemIds = this.flyerItemClippings.map((clipping) => clipping.flyerItemId);
		ops.forEach((op) => {
			if (op.verb === "post" && !op.object.id) {
				/* Post OP with no ID is a create, so just blindly add it.
					* Exception: if flyer_item_clipping type, we need to ensure the flyer_item_id it unique for the SL */
				if (existingItemIds.indexOf(op.object["flyer_item_id"]) !== -1) {
					return;
				}
				this._outstandingOps.push(op);
				return;
			}

			let arr: SLSyncable[] = [];
			if (op.object.type === "flyer_item_clipping") {
				arr = this.flyerItemClippings;
			} else if (op.object.type === "list_item") {
				arr = this.listItems;
			} else if (op.object.type === "photo") {
				arr = this.photos;
			}
			const match = array.find(arr, (item) => item.id === op.object.id);
			if (!match) {
				return;
			}

			if (op.object.type === "list_item") {
				(<SLListItem>match).checked = op.object["checked"];
			}
			this._outstandingOps.push(match.createOp(op.verb, true));
		});
	}

	createAllItemOps(localList: ShoppingList): ShoppingListOp[] {
		/* Helper function which creates post operations for all items and clippings in this shopping list */
		const result: ShoppingListOp[] =
			localList.items.map((item) => item.createOp("post"))
				.concat(localList.flyerItems.map((item) => item.createOp("post")))
				.concat(localList.ecomItems.map((item) => item.createOp("post")));
		return result;
	}

	createOp(verb: ShoppingListOpVerb): ShoppingListOp { /* N/A */ return null; }

	discardOutstandingOps() {
		this._outstandingOps = [];
	}

	private _addOperation(op: ShoppingListOp) {

		if (!this._delegate) {
			/* Don't need to create/add ops for local lists, but we still need to save */
			this.localSave();
			return;
		}

		/* Use a 'last operation wins' policy and remove any existing operations for the current id.
			* THis is neededed because we batch SL changes so we're not constantly pounding on the server,
			* and, because of this, so it's possible that multiple operations act on the same object
			* (eg: checking, unchecking, re-checking).
			*/
		const matchIdx = array.findIndex(this._outstandingOps,
			(currentOp) => op.object.id && currentOp.object.id === op.object.id);
		if (matchIdx > -1) {
			this._outstandingOps.splice(matchIdx, 1);
		}
		this._outstandingOps.push(op);
		this.localSave();
	}

	private _getArrayForItem(item: SLSyncable): SLSyncable[] {
		if (item instanceof SLListItem) {
			return this.listItems;
		} else if (item instanceof SLFlyerItemClipping) {
			return this.flyerItemClippings;
		} else if (item instanceof SLPhoto) {
			return this.photos;
		} else if (item instanceof SLEcomItem) {
			return this.ecomItems;
		}
		return [];
	}

	private _sortTerms() {
		/* Helper function to sort item names (we want them sorted alphabetically in the UI) */
		this.listItems.sort((a, b) => {
			const itemA = a.term.toLowerCase();
			const itemB = b.term.toLowerCase();
			if (itemA < itemB) {
				return -1;
			}
			if (itemA > itemB) {
				return 1;
			}
			return 0;
		});
	}
}

@service("shoppingListService")
export class ShoppingListService extends Service {

	/* Only update ecom items once per session - tracked using flag below */
	private static ecomItemsUpdated = false;

	@inject storage: StorageService;
	@inject location: LocationService;
	@inject eventService: EventService;
	@inject auth: AuthenticationService;
	@inject config: ConfigService;
	@inject categoryService: CategoryService;
	@inject backflipp: Backflipp;
	@inject toast: ToastService;

	private _localShoppingList: ShoppingList = null;
	private _syncTimer: Timer = new Timer(1000 * 15, this.doSync, this);
	private _joinOperation: Promise<ShoppingList>;
	private _shoppingHistoryCache: Promise<SLSuggestion[]> = null;


	private _popularsMap: {[key: string]: {canonicalCategory: string; iconAssetName: string}} = {
		"apple": { canonicalCategory: "produce", iconAssetName: "apple" },
		"bacon": { canonicalCategory: "meat_seafood", iconAssetName: "bacon" },
		"bananas": { canonicalCategory: "produce", iconAssetName: "banana" },
		"bread": { canonicalCategory: "bakery", iconAssetName: "bread" },
		"broccoli": { canonicalCategory: "produce", iconAssetName: "broccoli" },
		"butter": { canonicalCategory: "dairy_eggs", iconAssetName: "butter" },
		"cereal": { canonicalCategory: "pantry", iconAssetName: "cereal" },
		"cheese": { canonicalCategory: "dairy_eggs", iconAssetName: "cheese" },
		"chicken": { canonicalCategory: "meat_seafood", iconAssetName: "chicken" },
		"cucumber": { canonicalCategory: "produce", iconAssetName: "cucumbers" },
		"eggs": { canonicalCategory: "dairy_eggs", iconAssetName: "egg" },
		"lettuce": { canonicalCategory: "produce", iconAssetName: "lettuce" },
		"milk": { canonicalCategory: "dairy_eggs", iconAssetName: "milk" },
		"toilet_paper": { canonicalCategory: "household", iconAssetName: "toiletpaper" },
		"tomato": { canonicalCategory: "produce", iconAssetName: "tomato" },
		"yogurt": { canonicalCategory: "dairy_eggs", iconAssetName: "yogurt" }
	};

	constructor() {
		super();
		this._localShoppingList = this._loadShoppingList();
		window.addEventListener("flipp-auth", this._handleAuthEvent);
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			/* User is logged in - we can just start the sync timer */
			this.startSync();
		}
	}

	get localList() {
		return this._localShoppingList;
	}

	getCanonicalCategory(quickItem: string): string {

		let canonicalCategory = "unknown";
		if (quickItem && this._popularsMap[quickItem] && this._popularsMap[quickItem].canonicalCategory) {
			canonicalCategory = this._popularsMap[quickItem].canonicalCategory;
		}

		return canonicalCategory;
	}

	get quickAddItems() {
		// We only want the top 16 entries.
		const quickItems: SLQuickItem[] = Object.keys(this._popularsMap).slice(0, 16).map((itemName) => ({
				name: i18n(`item_${itemName}`),
				iconUrl: `https://f.wishabi.net/creative/Distribution/images/sl/happy/${itemName}.png`,
				canonicalCategory: this._popularsMap[itemName].canonicalCategory,
				iconAssetName: this._popularsMap[itemName].iconAssetName
			} as SLQuickItem));

		/* Filter out any quick add items if they've already been added to the users SL */
		// Also, filter out variations of the same items e.g. apples and apple.
		const localItems = this._localShoppingList.items.map((item) => item.term.toLowerCase());
		const items: SLQuickItem[] = quickItems.filter(entry => {
			return localItems.indexOf(entry.name.toLowerCase()) === -1 && !this.categoryService.iconAssetNameExists(entry.iconAssetName, localItems);
		});

		return items;
	}

	get historicalItems(): Promise<SLSuggestion[]> {
		return this._shoppingHistoryCache;
	}

	get allItems() {
		const localItems = this._localShoppingList.items.map((item) => item.term);
		return localItems.concat(this.quickAddItems.map((entry) => entry.name));
	}

	startSync() {
		this._syncTimer.start();
	}

	stopSync() {
		this._syncTimer.stop();
	}

	joinList(token: AccountsUser, inviteId: string, shoppingListId?: string) {
		const body: {[key: string]: string | string[]} = {
			"platform": "web",
			"invite_id": inviteId
		};
		const slIdSegment = shoppingListId ? `/${shoppingListId}` : "";
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/shopping_lists${slIdSegment}/join`,
			method: "POST",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<any>(opts, [200]).then((data) => new ShoppingList(data, true, this._handleListChanged));
	}

	unshareList(token: AccountsUser, listId: string) {
		const body: {[key: string]: string} = {
			"platform": "web"
		};
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/shopping_lists/${listId}`,
			method: "DELETE",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<any>(opts, [200]).then((data) => {
			const list = new ShoppingList({}, false, this._handleListChanged);
			this._localShoppingList = list;
			this._localShoppingList.localSave();
			return list;
		});
	}

	acceptInvite(token: AccountsUser, inviteId: string) {
		return new Promise((resolve, reject) => {
			this._getShoppingLists(token).then((listIds) => {
				const existingListId = listIds.length > 0 ? listIds[0] : null;
				this.joinList(token, inviteId, existingListId)
					.then((joinedList) => resolve(joinedList))
					.catch(reject);
			}).catch(reject);
		});
	}

	joinLocalList(token: AccountsUser) {
		if (this._joinOperation) {
			/* Return ongoing join operation if available (don't want to join multiple times) */
			return this._joinOperation;
		}
		this._joinOperation = new Promise((resolve, reject) => {
			/* Function to handle merging a local list with a server list. We need to do this when a user
				* user starts as unauthenticated, adds items to their SL and then authenticates. We
				* automatically take those items they added while unauthenticated and create ShoppingListOp
				* operations so they can be applied (merged) into the server list */

			this._getShoppingLists(token).then((lists) => {
				if (!lists || lists.length === 0) {
					/* User has no existing list - create one for them */
					this._createList(token).then((list) => {
						const ops = list.createAllItemOps(this._localShoppingList);
						this._doLocalJoin(token, list, ops).then((joinedList) => {
							this._localShoppingList = joinedList;
							this._localShoppingList.localSave();
							this._joinOperation = null;
							resolve(list);
						}).catch((err: Error) => {
							this._localShoppingList = list;
							this._localShoppingList.localSave();
							this._joinOperation = null;
							reject(err);
						});
					});
					return;
				} else {
					this._getShoppingList(token, lists[0]).then((list) => {
						const ops = list.createAllItemOps(this._localShoppingList);
						this._doLocalJoin(token, list, ops).then((joinedList) => {
							this._localShoppingList = joinedList;
							this._localShoppingList.localSave();
							this._joinOperation = null;
							resolve(list);
						}).catch((err: Error) => {
							this._localShoppingList = list;
							this._localShoppingList.localSave();
							this._joinOperation = null;
							reject(err);
						});
					}).catch((err: Error) => {
						this._joinOperation = null;
						reject(err);
					});
				}
			});
		});
		return this._joinOperation;
	}

	generateInvite(token: AccountsUser, list: ShoppingList) {
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/shopping_lists/${list.id}/invitation`,
			method: "GET",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			]
		};
		return fetch<InviteJSON>(opts, [200]);
	}

	reset() {
		this._shoppingHistoryCache = null;
		this._localShoppingList = new ShoppingList({}, false, this._handleListChanged);
		this._localShoppingList.localSave();
	}

	getShoppingListSuggestions(token: AccountsUser) {
		const userId = token.user_id;

		if (!this._shoppingHistoryCache) {
			const opts: FetchOptions = {
				url: `${this.config.ACCOUNTS_SERVER}/v1/users/${userId}/shopping_lists/suggestions`,
				method: "GET",
				headers: [
					{"Content-Type": "application/json"},
					{"Authorization": `Token token=${token.access_token}`}
				]
			};

			this._shoppingHistoryCache = fetch<SuggestionsJSON>(opts, [200])
				.then((resp: SuggestionsJSON) => {
				if (!resp.history || !Array.isArray(resp.history)) {
					// TODO: Error message
					throw new Error();
				}

				return resp.history.map((json) => new SLSuggestion(json));
			});
		}

		return this._shoppingHistoryCache;
	}

	updateEcomItems(): Promise<void> {
		if (ShoppingListService.ecomItemsUpdated) return;
		ShoppingListService.ecomItemsUpdated = true;

		const ecomItems = this.localList.ecomItems;
		if (!ecomItems || ecomItems.length === 0) return Promise.resolve(null);

		const itemIds = ecomItems.map((item) => `${item.ecomItemId}`);
		return new Promise<void>((resolve, reject) => {
			this.backflipp.getMultiEcomItems(itemIds).then((results) => {
				if (!results) {
					return resolve(null);
				}

				const resultsMap: {[key: string]: EcomItemSummaryJSON} = {};
				results.ecom_items.forEach((item) => {
					resultsMap[item.id] = item;
				});

				const payloadItems: SLEcomItem[] = [];
				const payloadData: any[] = [];
				itemIds.forEach((id) => {
					const slItem = array.find(this.localList.ecomItems, (item) => `${item.ecomItemId}` === id);
					if (resultsMap[id] && Number(resultsMap[id].current_price) !== Number(slItem.price)) {
						payloadItems.push(slItem);
						payloadData.push({
							id,
							current_price: resultsMap[id].current_price,
							expired: false
						});
					} else if (!resultsMap[id]) {
						// Ecom item no longer valid - set EXPIRES
						if (!slItem) return;
						payloadItems.push(slItem);
						payloadData.push({
							id,
							current_price: slItem.price,
							expired: true
						});
					}
				});

				if (payloadItems.length > 0) {
					this.localList.modify(payloadItems, payloadData);
				}

				resolve(null);
			});
		});
	}

	private doSync() {
		/* Local list must be a proxy for remote otherwise no point to sync */
		if (this.localList.isDelegate && this.auth.status === AuthStatus.LOGGED_IN) {
			if (this.localList.modified) {
				/* If list has outstanding modifications, try to apply them */
				this._applyOperations();
			} else {
				this._getShoppingList(this.auth.user.token, this.localList.id).then((serverList) => {
					if (this.localList.commitVersion !== serverList.commitVersion) {
						const familySyncEvent = this._isFamilySync(this.localList.users, serverList.users);
						this._localShoppingList = serverList;
						this._localShoppingList.localSave();
						this.eventService.fireShoppingListEvent(familySyncEvent ? "family" : "sync");
					}
					this.startSync();
				}).catch((err: Error) => {
					this.startSync();
				});
			}
		} else {
			this.startSync();
		}
	}

	private _doLocalJoin = (token: AccountsUser, list: ShoppingList, ops: ShoppingListOp[], retryNum = 0) => {
		if (retryNum === 2) {
			/* Don't allow more than 2 tries to post updates */
			return Promise.reject(list);
		}

		return new Promise<ShoppingList>((resolve, reject) => {
			if (ops.length === 0) {
				/* No ops to apply (user has nothing in their local list). Return */
				resolve(list);
				return;
			}

			const modifications = {
				commit_version: list.commitVersion,
				_ops: ops
			} as ShoppingListModify;
			this._updateList(token, list.id, modifications).then(() => {
				this._getShoppingList(token, list.id).then((newSL) => {
					resolve(newSL);
				}).catch((err: Error) => {
					reject(err);
				});
			}).catch((err: Error) => {
				if (err instanceof FetchError && err.status === 409) {
					try {
						const body = JSON.parse(err.body);
						const updatedList = new ShoppingList(body.existing_resource, true, this._handleListChanged);
						this._localShoppingList = updatedList;
						this._localShoppingList.localSave();
						if (body.message.toLowerCase().indexOf("exceeds limit") !== -1) {
							// TODO: fire an event here? See FW-694
							// this.eventService.fireToastEvent(i18n('shopping_list_exceeded_limit_error'), 'error');
							resolve(list);
							return;
						} else if (body.message.toLowerCase().indexOf("flyer_item_clippings") !== -1 ||
								body.message.toLowerCase().indexOf("ecom_item_clipping") !== -1) {
							/* Flyer items & ecom items must be unique in a shopping list (can only be clipped once), so dedup and try
								* again */
							const existingItemIds = updatedList.flyerItemClippings.map((clipping) => clipping.flyerItemId);
							const existingEcomIds = updatedList.ecomItems.map((item) => item.id);
							const newOps: ShoppingListOp[] = [];
							ops.forEach((op) => {
								if (op.object.type !== "flyer_item_clipping" && op.object.type !== "ecom_item_clipping") {
									newOps.push(op);
								} else if (op.object.type === "flyer_item_clipping" &&
										existingItemIds.indexOf(op.object["flyer_item_id"]) === -1) {
									newOps.push(op);
								} else if (op.object.type === "ecom_item_clipping" &&
										existingEcomIds.indexOf(op.object["ecom_item_id"]) === -1) {
									newOps.push(op);
								}
							});
							this._doLocalJoin(token, list, newOps, ++retryNum)
								.then((list) => resolve(list))
								.catch((err: Error) => reject(err));
						}
					} catch (e) {
						reject(err);
					}
					return;
				}
				reject(err);
			});
		});
	}

	private _isFamilySync(local: {id: string}[], server: {id: string}[]) {
		if (local.length !== server.length) {
			return true;
		}
		let differentUsers = false;
		local.forEach((user) => {
			const match = array.find(server, (item) => item.id === user.id);
			if (!match) {
				differentUsers = true;
			}
		});
		server.forEach((user) => {
			const match = array.find(local, (item) => item.id === user.id);
			if (!match) {
				differentUsers = true;
			}
		});
		return differentUsers;
	}

	private _applyOperations = (ops?: ShoppingListOp[], retryNum = 0) => {
		if (retryNum === MAX_UPDATE_RETRY) {
			this.toast.fireToastEvent(i18n("shopping_list_save_error"), ToastLevel.ERROR);
			this.localList.discardOutstandingOps();
			this.startSync();
			return;
		}

		const toApply = ops || this.localList.popOperations();
		const modifications = {
			commit_version: this.localList.commitVersion,
			_ops: toApply
		} as ShoppingListModify;
		this._updateList(this.auth.user.token, this.localList.id, modifications).then((data) => {
			this._getShoppingList(this.auth.user.token, this.localList.id).then((serverList) => {
				const familySyncEvent = this._isFamilySync(this._localShoppingList.users, serverList.users);
				/* Check if an there is a new operation (ie new user action while XHR was ongoing). If so,
					* immediately try to apply it */
				const outstandingOps = this.localList.popOperations();
				this._localShoppingList = serverList;
				this._localShoppingList.localSave();
				if (outstandingOps.length > 0) {
					this._localShoppingList.mergeOperations(outstandingOps);
					this._applyOperations(null, ++retryNum);
				} else {
					this.eventService.fireShoppingListEvent(familySyncEvent ? "family" : "sync");
					this.startSync();
				}
			}).catch((err: Error) => {
				this.startSync();
			});
		}).catch((err: Error) => {
			if (err instanceof FetchError && (err.status === 400 || err.status === 409)) {
				try {
					const body = JSON.parse(err.body);
					const updatedList = new ShoppingList(body.existing_resource, true, this._handleListChanged);
					this._localShoppingList = updatedList;
					this._localShoppingList.localSave();
					this.eventService.fireShoppingListEvent("sync");
					if (err.status === 400) {
						/* List commit_version was out of date - just retry the current SL ops (without modification) */
						this._applyOperations(toApply, ++retryNum);
					} else if (err.status === 409) {
						if (body.message.toLowerCase().indexOf("exceeds limit") !== -1) {
							this.toast.fireToastEvent(i18n("shopping_list_exceeded_limit_error"), ToastLevel.ERROR);
							return;
						}
						/* List commit_version is valid, but an individual item commit_version is out of date.
							* Need to updated the operations so they apply cleanly on the current SL */
						this._localShoppingList.mergeOperations(toApply);
						this._applyOperations(null, ++retryNum);
					}
					return;
				} catch (e) {}
			}
		});
	}

	private _getShoppingLists(token: AccountsUser) {
		const body: {[key: string]: string | string[]} = {
			"platform": "web"
		};
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/shopping_lists`,
			method: "GET",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<{"shopping_lists": SLSyncable[]}>(opts, [200])
			.then((resp: {"shopping_lists": SLSyncable[]}) => {
			if (!resp.shopping_lists || !Array.isArray(resp.shopping_lists)) {
				// TODO: Error message
				throw new Error();
			}
			return resp.shopping_lists.map((list) => list.id);
		});
	}

	private _getShoppingList(token: AccountsUser, listId: string) {
		const body: {[key: string]: string | string[]} = {
			"platform": "web"
		};
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/shopping_lists/${listId}`,
			method: "GET",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			],
			body: JSON.stringify(body)
		};
		return fetch<any>(opts, [200]).then((data) => new ShoppingList(data, true, this._handleListChanged));
	}

	private _updateList = (token: AccountsUser, listId: string, ops: ShoppingListModify) => {
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/shopping_lists/${listId}`,
			method: "PUT",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			],
			body: JSON.stringify(ops)
		};

		/* FIXME TODO This typing is WRONG */
		return fetch<any>(opts, [200]).then((data) => new ShoppingList(data, true, this._handleListChanged));
	}

	private _createList = (token: AccountsUser) => {
		const opts: FetchOptions = {
			url: `${this.config.ACCOUNTS_SERVER}/v1/users/${token.user_id}/shopping_lists`,
			method: "POST",
			headers: [
				{"Content-Type": "application/json"},
				{"Authorization": `Token token=${token.access_token}`}
			]
		};
		return fetch<any>(opts, [200]).then((data) => new ShoppingList(data, true, this._handleListChanged));
	}

	private _loadShoppingList() {
		const users = JSON.parse(Cookies.get("users") || "[]");
		const shoppingList = JSON.parse(this.storage.getItem("shopping_list") || "{}");
		const data = {...shoppingList, users};
		/* For proxy lists (server-based), the _proxy flag will be set to true. We don't want to
			* initialize a local shopping list based on a server list, so use empty data where necessary */
		return new ShoppingList(data._delegate ? {} : data, false, this._handleListChanged);
	}

	private _handleAuthEvent = (e: CustomEvent) => {
		if (e.detail.action === "login") {
			if (this._joinOperation) {
				return this._joinOperation.then((list) => {
					this.eventService.fireShoppingListEvent("join");
					this.startSync();
					return list;
				});
			}

			this._joinOperation = this.joinLocalList(this.auth.user.token).then((list) => {
				this.eventService.fireShoppingListEvent("join");
				this._joinOperation = null;
				this.startSync();
				return list;
			});
		} else if (e.detail.action === "logout") {
			this.reset();
			this.stopSync();
			this.eventService.fireShoppingListEvent("reset");
		}
	}

	private _handleListChanged = () => {
		if (this._syncTimer.running) {
			/* Sync timer will be 'running' when a sync operation is NOT ongoing. In this case,
				* fire the timer and start sync automatically */
			this._syncTimer.fire();
		}


		if (this.auth.status !== AuthStatus.LOGGED_IN) {
			// We need to update the SL count at the top of the page (RHS corner).
			// ----------------------------------------------------------------------
			this.eventService.fireShoppingListEvent("sync");
		}
	}
}
